import {
  getErrors,
  getResponses,
  isResponseOrError,
  ResponseOrError,
  wrapError,
  wrapSuccess
} from '../../types/responses';

export type SyncMapFunction<T, U> = (
  item: T,
  index: number,
  array: T[]
) => ResponseOrError<U> | U;

export const everySync = <T>(
  arr: T[],
  fn: SyncMapFunction<T, boolean>
): ResponseOrError<boolean> => {
  const [errorMapping, results] = mapSync(arr, fn);

  if (errorMapping) {
    return wrapError(errorMapping);
  }

  return wrapSuccess(results.every(Boolean));
};

export const mapSync = <T, U>(
  arr: T[],
  mapFn: SyncMapFunction<T, U>
): ResponseOrError<U[]> => {
  const results = arr.map((val, idx, arr) => {
    try {
      const result = mapFn(val, idx, arr);

      if (isResponseOrError(result)) {
        return result;
      }

      return wrapSuccess(result);
    } catch (err) {
      return wrapError(err as Error);
    }
  });

  const errors = getErrors(results);

  if (errors.length) {
    return wrapError(new Error(errors.map(e => e.message).join(', ')));
  }

  return wrapSuccess(getResponses(results));
};

export const someSync = <T>(
  arr: T[],
  fn: SyncMapFunction<T, boolean>
): ResponseOrError<boolean> => {
  const [errorMapping, results] = mapSync(arr, fn);

  if (errorMapping) {
    return wrapError(errorMapping);
  }

  return wrapSuccess(results.some(Boolean));
};

export type SyncReduceFunction<T, U> = (
  previousValue: U,
  currentValue: T,
  currentIndex: number,
  array: T[]
) => ResponseOrError<U> | U;

export const reduceSync = <T, U>(
  arr: T[],
  reduceFn: SyncReduceFunction<T, U>,
  initialValue: U
): ResponseOrError<U> => {
  const result = arr.reduce<ResponseOrError<U> | U>((acc, val, idx, arr) => {
    try {
      let previousValue: U;

      if (isResponseOrError(acc)) {
        if (acc.error) {
          return acc;
        }

        previousValue = acc.response;
      } else {
        previousValue = acc;
      }

      const result = reduceFn(previousValue, val, idx, arr);

      if (isResponseOrError(result)) {
        return result;
      }

      return wrapSuccess(result);
    } catch (err) {
      return wrapError(err as Error);
    }
  }, initialValue);

  if (isResponseOrError(result)) {
    return result;
  }

  return wrapSuccess(result);
};
